/* eslint-disable sonarjs/cognitive-complexity */
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getWishListTrackingObj } from '@tcp/core/src/utils/utils.web';
import { setLoyaltyLocation } from '@tcp/core/src/constants/analytics';
import { getCurrentCurrencySymbol } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { getCurrencyAttributes } from '@tcp/core/src/reduxStore/selectors/currency.selectors';
import { FavoritesIcons } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/views/ProductItemComponents.jsx';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { GlobalAccessibilityLabels } from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import {
  getPriceData,
  getWishListData,
  getOfferPrice,
  getOfferPriceRangeAccessibilityLabel,
  getOfferPriceAccessibilityLabel,
} from './plpTileHelpers/priceAndWishlistHelper';
import {
  AddToWishListButton,
  MerchantTag,
  OfferPrice,
  StrikedThroughPrice,
} from './styles/PriceAndWishList.style';
import plpTileLocators from './plpTileLocators';
import useHasMounted from '../../../../../hooks/useHasMounted';

const getTileRedesignClass = (isTileRedesignAB) => {
  return isTileRedesignAB ? ' tile-redesign' : '';
};

const PriceAndWishList = ({
  wishListData,
  onAddItemToFavorites,
  onSetLastDeletedItemIdAction,
  minListPrice,
  minOfferPrice,
  lowOfferPrice,
  highOfferPrice,
  lowListPrice,
  highListPrice,
  productPartNo,
  merchantTag,
  primaryBrand,
  trackWishListClick,
  isLoggedIn,
  currencySymbol,
  currencyAttributes,
  isBundle,
  isHlpTile,
  isPjpTile,
  isHideOriginalPrice,
  isShowPriceRangeEnabled,
  isPLP,
  isTileRedesignAB,
  setPLPSLPAddToCartCTA,
  isSLPTile,
}) => {
  const hasMounted = useHasMounted();
  const {
    offerPrice,
    listPrice,
    lowestOfferPrice,
    highestOfferPrice,
    lowestListPrice,
    highestListPrice,
    isShowListPrice,
    isShowOfferPriceRange,
    isShowListPriceRange,
  } = useMemo(
    () =>
      getPriceData({
        minListPrice,
        minOfferPrice,
        lowOfferPrice,
        highOfferPrice,
        lowListPrice,
        highListPrice,
        currencySymbol,
        currencyAttributes,
      }),
    [
      minListPrice,
      minOfferPrice,
      lowOfferPrice,
      highOfferPrice,
      lowListPrice,
      highListPrice,
      currencySymbol,
      currencyAttributes,
    ]
  );

  const showOfferPrice = useMemo(
    () =>
      getOfferPrice(
        isShowOfferPriceRange,
        isShowListPriceRange,
        lowestOfferPrice,
        highestOfferPrice,
        lowestListPrice,
        highestListPrice,
        lowOfferPrice
      ),
    [
      isShowOfferPriceRange,
      isShowListPriceRange,
      lowestOfferPrice,
      highestOfferPrice,
      lowestListPrice,
      highestListPrice,
    ]
  );
  const { isFavorite, wishList } = useMemo(
    () =>
      getWishListData({
        productPartNo,
        wishListData,
        primaryBrand,
      }),
    [productPartNo, wishListData, primaryBrand]
  );

  const addOrRemoveItemToFavorites = () => {
    const loyaltyLocation = `favorites| ${isSLPTile ? 'SLP' : 'PLP'}}`;
    setLoyaltyLocation(loyaltyLocation);
    const wishListObj = getWishListTrackingObj(loyaltyLocation, isLoggedIn);
    trackWishListClick(wishListObj);
    if (!isFavorite) {
      onAddItemToFavorites({
        colorProductId: productPartNo,
        page: isSLPTile ? 'SLP' : 'PLP',
        products: null,
      });
    } else {
      onSetLastDeletedItemIdAction({
        externalId: wishList?.id,
        productId: productPartNo,
        itemId: wishList.itemId,
        notFromFavoritePage: true,
        wishListName: wishList?.displayName,
      });
    }
  };

  const isShowRangedPrice = () => {
    return isShowPriceRangeEnabled && (isShowOfferPriceRange || isShowListPriceRange);
  };

  const classCheck = () =>
    isTileRedesignAB && setPLPSLPAddToCartCTA !== 'icon' ? 'offer-price' : '';
  const strikeThroughCheck = () =>
    isTileRedesignAB && setPLPSLPAddToCartCTA !== 'icon' ? 'striked-price' : '';

  const shouldRenderListPrice = hasMounted && listPrice !== offerPrice && !isHideOriginalPrice;
  const shouldRenderListPriceRange =
    !isShowListPriceRange && isShowListPrice && !isHideOriginalPrice;

  const accessibilityLabels = getLabelFromCopyOrFromTranslation(
    'global.accessibility',
    GlobalAccessibilityLabels
  );

  const priceLabel = getLabelFromCopyOrFromTranslation('PLP.PLP_sort_filter.lbl_price', PLPLabels);
  const salePriceLabel = renderLabel(accessibilityLabels.lbl_sale_price, 'lbl_sale_price');
  const originalPriceLabel = renderLabel(
    accessibilityLabels.lbl_original_price,
    'lbl_original_price'
  );
  const toLabel = renderLabel(accessibilityLabels.lbl_to, 'lbl_to');

  return !isBundle && !isShowRangedPrice() ? (
    <div className={`flex-row justify-between ${isHlpTile ? 'hlp-tile-price-wishlist' : ''}`}>
      <div
        className={`price-list${getTileRedesignClass(isTileRedesignAB)}`}
        data-locator={plpTileLocators.productPrice}
      >
        <OfferPrice brand={primaryBrand} className={classCheck()}>
          <span className="sr-only">
            {`${getOfferPriceAccessibilityLabel({
              shouldRenderListPrice,
              priceLabel,
              salePriceLabel,
            })} ${offerPrice}`}
          </span>
          <span aria-hidden="true">{offerPrice}</span>
        </OfferPrice>
        {shouldRenderListPrice ? (
          <>
            <StrikedThroughPrice>
              <span className="sr-only">{`${originalPriceLabel}: ${listPrice}`}</span>
              <span aria-hidden="true">{listPrice}</span>
            </StrikedThroughPrice>

            {!isHlpTile && merchantTag && (
              <MerchantTag setPLPSLPAddToCartCTA={setPLPSLPAddToCartCTA}>{merchantTag}</MerchantTag>
            )}
          </>
        ) : (
          <></>
        )}
      </div>
      {!isHlpTile && !isPjpTile && !isPLP && (
        <AddToWishListButton onClick={addOrRemoveItemToFavorites}>
          <FavoritesIcons isSolidIcon={isFavorite} />
        </AddToWishListButton>
      )}
    </div>
  ) : (
    <div className="flex-row">
      <div
        className={`price-list bundle-price${getTileRedesignClass(isTileRedesignAB)}`}
        data-locator={plpTileLocators.productPrice}
      >
        <OfferPrice brand={primaryBrand} isBundle={isBundle} className={classCheck()}>
          <span className="sr-only">
            {getOfferPriceRangeAccessibilityLabel({
              showOfferPrice,
              shouldRenderListPriceRange,
              priceLabel,
              salePriceLabel,
              toLabel,
            })}
          </span>
          <span aria-hidden="true">{showOfferPrice}</span>
        </OfferPrice>
        {shouldRenderListPriceRange ? (
          <>
            <div className={strikeThroughCheck()}>
              <span className="sr-only">
                {`${originalPriceLabel}: ${lowestListPrice} ${toLabel} ${highestListPrice}`}
              </span>
              <StrikedThroughPrice aria-hidden="true">{lowestListPrice}</StrikedThroughPrice>
              <span aria-hidden="true">&nbsp;-&nbsp; </span>
              <StrikedThroughPrice aria-hidden="true">{highestListPrice}</StrikedThroughPrice>
            </div>

            {!isHlpTile && merchantTag && <MerchantTag>{merchantTag}</MerchantTag>}
          </>
        ) : null}
      </div>
    </div>
  );
};

PriceAndWishList.propTypes = {
  wishListData: PropTypes.shape({}),
  onAddItemToFavorites: PropTypes.func.isRequired,
  onSetLastDeletedItemIdAction: PropTypes.func.isRequired,
  minListPrice: PropTypes.number,
  minOfferPrice: PropTypes.number,
  productPartNo: PropTypes.string,
  merchantTag: PropTypes.string,
  primaryBrand: PropTypes.string,
  currencySymbol: PropTypes.string,
  currencyAttributes: PropTypes.shape({}),
  trackWishListClick: PropTypes.func,
  isPLP: PropTypes.bool,
  isSLPTile: PropTypes.bool,
  isShowPriceRangeEnabled: PropTypes.bool,
};
PriceAndWishList.defaultProps = {
  wishListData: {},
  minListPrice: 0,
  minOfferPrice: 0,
  productPartNo: '',
  merchantTag: '',
  primaryBrand: 'tcp',
  currencySymbol: '$',
  currencyAttributes: {},
  trackWishListClick: () => {},
  isPLP: false,
  isSLPTile: false,
  isShowPriceRangeEnabled: false,
};

const mapStateToProps = (state) => {
  return {
    currencySymbol: getCurrentCurrencySymbol(state),
    currencyAttributes: getCurrencyAttributes(state),
  };
};

export default connect(mapStateToProps)(PriceAndWishList);
export { PriceAndWishList as PriceAndWishListVanilla };
